// The moderation pages' pure helpers: labels for reasons, states and
// people. No DOM.
import type { AdminSchemas } from "@10twenty/api/admin";

export type ReportSummary = AdminSchemas["ReportSummary"];
export type ReportReason = AdminSchemas["ReportReason"];
export type ReportedMessage = AdminSchemas["ReportedMessage"];

export const REASON_LABELS: Record<ReportReason, string> = {
  spam: "Spam",
  harassment: "Harassment",
  hate: "Hate",
  violence: "Violence",
  sexual: "Sexual content",
  self_harm: "Self-harm",
  illegal: "Illegal",
  other: "Other",
};

/** "Open", "Dismissed", "Message removed", "Photo removed", "Account suspended". */
export function reportStatusLabel(report: Pick<ReportSummary, "status" | "resolution">): string {
  if (report.status === "open") return "Open";
  if (report.status === "dismissed") return "Dismissed";
  if (report.resolution === "message_removed") return "Message removed";
  if (report.resolution === "avatar_removed") return "Photo removed";
  if (report.resolution === "account_suspended") return "Account suspended";
  return "Actioned";
}

/** The reported account's name, "Deleted account" once it is gone. */
export function targetLabel(target: ReportSummary["target"]): string {
  if (target.name === null) return "Deleted account";
  return target.user_id ? target.name : `${target.name} (deleted)`;
}

const STATE_LABELS: Record<ReportedMessage["state"], string> = {
  visible: "Still visible",
  removed: "Removed (unsent or by a moderator)",
  gone: "Deleted since",
};

export function messageStateLabel(state: ReportedMessage["state"]): string {
  return STATE_LABELS[state];
}

/** Where the message was sent: "Circle chat · Demo Family", "Direct message". */
export function conversationLabel(message: Pick<ReportedMessage, "conversation_kind" | "circle">): string {
  const kind =
    message.conversation_kind === "direct"
      ? "Direct message"
      : message.conversation_kind === "event"
        ? "Event chat"
        : message.conversation_kind === "circle"
          ? "Circle chat"
          : "Chat";
  return message.circle ? `${kind} · ${message.circle.name ?? "deleted circle"}` : kind;
}

/** "Still their photo" while the reported photo is current, else "Since replaced or removed". */
export function photoStateLabel(photo: AdminSchemas["ReportedPhoto"]): string {
  return photo.current ? "Still their photo" : "Since replaced or removed";
}

/** Remove-photo applies to an open report whose kept photo is still theirs. */
export function canRemovePhoto(report: Pick<AdminSchemas["ReportDetail"], "status" | "photo" | "target">): boolean {
  return report.status === "open" && report.target.user_id !== null && report.photo?.current === true;
}

/** Remove-message applies while the message still exists and isn't already removed. */
export function canRemoveMessage(message: ReportedMessage | null): boolean {
  return message !== null && message.state === "visible";
}
